import { api } from './client';
import type {
  AccountAdmin,
  Contact,
  EntityDetail,
  SchoolSearchResponse,
  Subscription,
} from './types';

export interface SchoolSearchParams {
  name?: string;
  nid?: string;
  nces?: string;
  state?: string;
  letter?: string;
  accountStatus?: string;
  nearNid?: string;
  radiusMiles?: number | string;
  schoolType?: number | string;
  religious?: number | string;
  minEnrollment?: number | string;
  limit?: number;
  offset?: number;
}

export function searchSchools(params: SchoolSearchParams = {}): Promise<SchoolSearchResponse> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  return api.get<SchoolSearchResponse>(`/api/v1/schools?${qs.toString()}`);
}

export function getSchool(nid: string): Promise<{ school: EntityDetail }> {
  return api.get(`/api/v1/schools/${encodeURIComponent(nid)}`);
}

export function createSchool(body: Record<string, any>): Promise<{ school: EntityDetail }> {
  return api.post(`/api/v1/schools`, body);
}

export function updateSchool(nid: string, body: Record<string, any>): Promise<{ school: EntityDetail }> {
  return api.put(`/api/v1/schools/${encodeURIComponent(nid)}`, body);
}

export function closeSchool(nid: string): Promise<unknown> {
  return api.del(`/api/v1/schools/${encodeURIComponent(nid)}`);
}

export function updateSchoolAccount(nid: string, body: Record<string, any>): Promise<{ school: EntityDetail }> {
  return api.put(`/api/v1/schools/${encodeURIComponent(nid)}/account`, body);
}

export function getSchoolContacts(nid: string): Promise<{ contacts: Contact[] }> {
  return api.get(`/api/v1/schools/${encodeURIComponent(nid)}/contacts`);
}

export function createSchoolContact(nid: string, body: Record<string, any>): Promise<{ contact: Contact }> {
  return api.post(`/api/v1/schools/${encodeURIComponent(nid)}/contacts`, body);
}

export function updateSchoolContact(
  nid: string,
  id: number,
  body: Record<string, any>,
): Promise<{ contact: Contact }> {
  return api.put(`/api/v1/schools/${encodeURIComponent(nid)}/contacts/${id}`, body);
}

export function deleteSchoolContact(nid: string, id: number): Promise<unknown> {
  return api.del(`/api/v1/schools/${encodeURIComponent(nid)}/contacts/${id}`);
}

export function updateSchoolAccountAdmin(
  nid: string,
  body: Record<string, any>,
): Promise<{ accountAdmin: AccountAdmin }> {
  return api.put(`/api/v1/schools/${encodeURIComponent(nid)}/account-admin`, body);
}

export function getSchoolSubscriptions(nid: string): Promise<{ subscriptions: Subscription[] }> {
  return api.get(`/api/v1/schools/${encodeURIComponent(nid)}/subscriptions`);
}

export function addSchoolSubscription(nid: string, body: Record<string, any>): Promise<{ subscription: Subscription }> {
  return api.post(`/api/v1/schools/${encodeURIComponent(nid)}/subscriptions`, body);
}

export function updateSchoolSubscription(
  nid: string,
  productId: number,
  body: Record<string, any>,
): Promise<{ subscription: Subscription }> {
  return api.put(`/api/v1/schools/${encodeURIComponent(nid)}/subscriptions/${productId}`, body);
}

export function deleteSchoolSubscription(nid: string, productId: number): Promise<unknown> {
  return api.del(`/api/v1/schools/${encodeURIComponent(nid)}/subscriptions/${productId}`);
}
